// components/FloatingAccessibilityControls.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Settings,
  Eye,
  EyeOff,
  Minus,
  Plus,
  X,
  Volume2,
  VolumeX,
  Mic,
  MicOff,
  ChevronDown,
  ChevronUp,
  Grip,
  Headphones,
} from 'lucide-react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useLanguageContext } from '../navigations/homenav';

const FloatingAccessibilityControls = ({ position = 'left-center' }) => {
  const {
    contrastMode,
    textSize,
    toggleContrastMode,
    setTextSizeMode,
    isScreenReaderEnabled,
    isVoiceCommandsEnabled,
    voiceType,
    isListening,
    isReading,
    toggleScreenReader,
    toggleVoiceCommands,
    updateVoiceType,
    startVoiceRecognition,
    readPageContent,
  } = useAccessibility();
  const { t } = useLanguageContext();

  const [showControls, setShowControls] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [panelPosition, setPanelPosition] = useState(null); // {x, y} or null
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);

  const panelRef = useRef(null);
  const dragRef = useRef(null);

  const handleToggle = () => setShowControls(!showControls);
  const handleClose = () => setShowControls(false);

  // Position styles based on the position prop (for initial position only)
  const buttonPositionClasses = {
    'left-center': 'fixed top-2/3 left-0 -translate-y-1/2',
    'top-right': 'fixed top-4 right-4',
  };

  const panelInitialClasses =
    position === 'left-center'
      ? 'fixed top-2/3 left-16 -translate-y-1/2'
      : 'fixed top-4 right-20';

  // Voice options
  const voiceOptions = [
    { value: 'default', label: t('accVoiceDefault') },
    { value: 'female', label: t('accVoiceFemale') },
    { value: 'male', label: t('accVoiceMale') },
    { value: 'slow', label: t('accVoiceSlow') },
    { value: 'fast', label: t('accVoiceFast') },
  ];

  // Drag functionality
  const handleMouseDown = (e) => {
    if (!panelRef.current) return;
    if (e.target === dragRef.current || dragRef.current.contains(e.target)) {
      setIsDragging(true);
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && panelRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - (panelRef.current.offsetWidth || 320);
      const maxY = window.innerHeight - (panelRef.current.offsetHeight || 400);

      setPanelPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging]);


  return (
    <>
      {/* Floating Accessibility Button */}
      <button
        onClick={handleToggle}
        className={`text-white flex items-center justify-center h-14 w-14 rounded-xl shadow-xl z-[100] transition-all duration-300 ease-in-out transform hover:scale-105 bg-gradient-to-r from-orange-600 to-orange-700 ${buttonPositionClasses[position]}`}
        aria-label={showControls ? t('accHideControlsAria') : t('accShowControlsAria')}
      >
        <Settings
          className={`transition-transform duration-500 ${showControls ? 'rotate-180' : ''}`}
          size={24}
        />
      </button>

      {/* Accessibility Controls Panel */}
      {showControls && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[99]"
            onClick={handleClose}
          />

          {/* Draggable Panel */}
          <div
            ref={panelRef}
            className={`bg-white rounded-xl shadow-2xl border border-gray-200 z-[101] min-w-80 max-h-[80vh] overflow-y-auto cursor-move ${
              !panelPosition ? panelInitialClasses : ''
            }`}
            style={
              panelPosition
                ? {
                    position: 'fixed',
                    left: `${panelPosition.x}px`,
                    top: `${panelPosition.y}px`,
                    transform: 'none',
                  }
                : {}
            }
            onMouseDown={handleMouseDown}
          >
            <div className="flex flex-col">
              {/* Drag Handle */}
              <div
                ref={dragRef}
                className="flex items-center justify-center p-2 bg-gray-50 rounded-t-xl border-b border-gray-200 cursor-move hover:bg-gray-100 transition-colors"
              >
                <Grip className="w-4 h-4 text-gray-500" />
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">{t('accPanelTitle')}</h3>
                    <button
                      onClick={handleClose}
                      className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                      aria-label={t('accCloseAria')}
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  {/* Screen Reader Toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center gap-2">
                      {isScreenReaderEnabled ? (
                        <Volume2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <VolumeX className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="font-medium text-gray-700">{t('accScreenReader')}</span>
                    </div>
                    <button
                      onClick={toggleScreenReader}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isScreenReaderEnabled ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                      aria-label={
                        isScreenReaderEnabled ? t('accScreenReaderDisableAria') : t('accScreenReaderEnableAria')
                      }
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isScreenReaderEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>


                  {/* Read Page Button (screen reader action) */}
                  <button
                    onClick={readPageContent}
                    disabled={!isScreenReaderEnabled}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                      isScreenReaderEnabled
                        ? 'bg-sky-600 text-white hover:bg-sky-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <Headphones className="w-4 h-4" />
                    {isReading ? t('accReading') : t('accReadThisPage')}
                  </button>

                  {/* Voice Commands Toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center gap-2">
                      {isVoiceCommandsEnabled ? (
                        <Mic
                          className={`w-5 h-5 ${
                            isListening ? 'text-red-600 animate-pulse' : 'text-green-600'
                          }`}
                        />
                      ) : (
                        <MicOff className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="font-medium text-gray-700">{t('accVoiceCommands')}</span>
                    </div>
                    <button
                      onClick={toggleVoiceCommands}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isVoiceCommandsEnabled ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                      aria-label={
                        isVoiceCommandsEnabled ? t('accVoiceDisableAria') : t('accVoiceEnableAria')
                      }
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isVoiceCommandsEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Voice Command Button */}
                  <button
                    onClick={startVoiceRecognition}
                    disabled={!isVoiceCommandsEnabled}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                      isVoiceCommandsEnabled
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <Mic className="w-4 h-4" />
                    {isListening ? t('accListening') : t('accStartVoice')}
                  </button>

                  {/* Voice Settings */}
                  {isScreenReaderEnabled && (
                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <button
                        onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mb-2 w-full text-left"
                      >
                        {t('accVoiceSettings')}
                        {showVoiceSettings ? (
                          <ChevronUp className="w-4 h-4 ml-auto" />
                        ) : (
                          <ChevronDown className="w-4 h-4 ml-auto" />
                        )}
                      </button>


                      {showVoiceSettings && (
                        <div className="pl-4 space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('accVoiceType')}</label>
                            <select
                              value={voiceType}
                              onChange={(e) => updateVoiceType(e.target.value)}
                              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {voiceOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Contrast Mode */}
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-gray-700">{t('accContrastMode')}</label>
                    <button
                      onClick={toggleContrastMode}
                      className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                        contrastMode === 'high-contrast'
                          ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {contrastMode === 'high-contrast' ? (
                          <Eye className="w-5 h-5" />
                        ) : (
                          <EyeOff className="w-5 h-5" />
                        )}
                        <span className="font-medium">
                          {contrastMode === 'high-contrast' ? t('accHighContrast') : t('accDefaultContrast')}
                        </span>
                      </div>
                      <div
                        className={`w-3 h-3 rounded-full ${
                          contrastMode === 'high-contrast' ? 'bg-yellow-400' : 'bg-gray-400'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Text Size */}
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-gray-700">{t('accTextSize')}</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTextSizeMode('small')}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all ${
                          textSize === 'small'
                            ? 'bg-orange-500 border-orange-500 text-white'
                            : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                        }`}
                        aria-label={t('accSmallTextAria')}
                      >
                        <Minus className="w-4 h-4 mx-auto" />
                        <span className="text-xs mt-1 block">A-</span>
                      </button>

                      <button
                        onClick={() => setTextSizeMode('default')}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all ${
                          textSize === 'default'
                            ? 'bg-orange-500 border-orange-500 text-white'
                            : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                        }`}
                        aria-label={t('accDefaultTextAria')}
                      >
                        <span className="text-sm font-bold">A</span>
                      </button>


                      <button
                        onClick={() => setTextSizeMode('large')}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all ${
                          textSize === 'large'
                            ? 'bg-orange-500 border-orange-500 text-white'
                            : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                        }`}
                        aria-label={t('accLargeTextAria')}
                      >
                        <Plus className="w-4 h-4 mx-auto" />
                        <span className="text-xs mt-1 block">A+</span>
                      </button>
                    </div>
                  </div>

                  {/* Voice Commands Help */}
                  {isVoiceCommandsEnabled && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-xs text-blue-800">
                        <p className="font-medium mb-1">{t('accExamplesTitle')}</p>
                        <ul className="space-y-1">
                          <li>• {t('accExReadPage')}</li>
                          <li>• {t('accExGoHome')}</li>
                          <li>• {t('accExGoWhoWeAre')}</li>
                          <li>• {t('accExOpenPrograms')}</li>
                          <li>• {t('accExOpenGallery')}</li>
                          <li>• {t('accExGoContact')}</li>
                          <li>• {t('accExIncreaseDecreaseText')}</li>
                          <li>• {t('accExHighNormalContrast')}</li>
                          <li>• {t('accExScroll')}</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FloatingAccessibilityControls;
